import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ArrowUpRight, Sparkles, Pen, Video, Menu, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className="fixed top-0 left-0 right-0 z-50 glass h-20 flex items-center justify-between px-6 md:px-12 lg:px-24"
    >
      <a
        href="/"
        data-testid="nav-logo"
        className="font-syne font-bold text-xl text-white tracking-tight"
      >
        BLANC<span className="text-brand-lime">SPACE</span>
      </a>

      {/* Desktop Nav */}
      <div className="hidden md:flex items-center gap-10">
        <button
          onClick={() => scrollToSection("services")}
          className="nav-link"
          data-testid="nav-services"
        >
          Services
        </button>
        <button
          onClick={() => scrollToSection("contact")}
          className="nav-link"
          data-testid="nav-contact"
        >
          Contact
        </button>
      </div>

      <button
        onClick={() => scrollToSection("contact")}
        data-testid="nav-cta"
        className="hidden md:flex items-center gap-2 px-6 py-3 border border-white/20 rounded-full text-white font-mono text-sm uppercase tracking-wider hover:bg-white/10 transition-colors"
      >
        Let's Talk
      </button>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden text-white"
        data-testid="nav-mobile-toggle"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="absolute top-20 left-0 right-0 glass p-6 md:hidden flex flex-col gap-4">
          <button
            onClick={() => scrollToSection("services")}
            className="nav-link text-left py-2"
          >
            Services
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="nav-link text-left py-2"
          >
            Contact
          </button>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Gradient orbs */}
      <div className="hero-gradient-orb-1" />
      <div className="hero-gradient-orb-2" />
      <div className="hero-gradient-orb-3" />

      <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto text-center">
        <div className="fade-in-up">
          <span className="tech-label mb-8 inline-block">
            GenZ AI Content Powerhouse
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-syne font-extrabold text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight mb-8 fade-in-up stagger-1"
        >
          CREATIVE INPUT.
          <br />
          INFINITE OUTPUT<span className="cursor-blink" />
        </h1>

        <p
          data-testid="hero-subtext"
          className="font-mono text-zinc-400 text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed fade-in-up stagger-2"
        >
          // We fuse human creativity with AI precision to craft content that
          breaks the algorithm. Social-first. Platform-native. Zero fluff.
        </p>

        <button
          onClick={scrollToContact}
          data-testid="hero-cta"
          className="cta-button fade-in-up stagger-3"
        >
          Launch A Project
          <ArrowUpRight size={20} />
        </button>

        <div className="mt-20 flex flex-wrap justify-center gap-8 md:gap-16 text-zinc-500 font-mono text-sm uppercase tracking-widest fade-in-up stagger-4">
          <span>Social Media</span>
          <span>Brand Identity</span>
          <span>Motion Design</span>
          <span>AI Content</span>
        </div>
      </div>
    </section>
  );
};

// Services Bento Grid
const ServicesSection = () => {
  const services = [
    {
      id: "social-genesis",
      title: "Social Genesis",
      subtitle: "AI Graphics",
      description:
        "Thumb-stopping visuals engineered for engagement. We train our AI on what works, then let creativity take over.",
      icon: <Sparkles size={24} />,
      image:
        "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&auto=format&fit=crop",
    },
    {
      id: "copylab",
      title: "CopyLab",
      subtitle: "AI Writing",
      description:
        "Words that convert. Hooks that stick. AI-assisted copy that sounds human because we keep the humans in charge.",
      icon: <Pen size={24} />,
      image:
        "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&auto=format&fit=crop",
    },
    {
      id: "video-reactor",
      title: "Video Reactor",
      subtitle: "Motion Design",
      description:
        "Reels, shorts, TikToks. Motion content that stops the scroll and keeps eyes locked. Fast turnaround, cinematic quality.",
      icon: <Video size={24} />,
      image:
        "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&auto=format&fit=crop",
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <span className="tech-label mb-4 inline-block">What We Do</span>
          <h2
            data-testid="services-title"
            className="font-syne font-bold text-4xl md:text-5xl text-white"
          >
            Digital Alchemy
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              data-testid={`service-card-${service.id}`}
              className="service-card glass-card rounded-3xl overflow-hidden bento-item"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative h-64 md:h-80">
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-image absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              </div>
              <div className="relative z-10 p-8 -mt-20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center text-brand-lime">
                    {service.icon}
                  </div>
                  <span className="font-mono text-xs text-brand-lime uppercase tracking-widest">
                    {service.subtitle}
                  </span>
                </div>
                <h3 className="font-syne font-bold text-2xl md:text-3xl text-white mb-3">
                  {service.title}
                </h3>
                <p className="font-mono text-sm text-zinc-400 leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (detail && Array.isArray(detail)) {
          const emailError = detail.find(err => err.loc?.includes('email'));
          if (emailError) {
            toast.error("Please enter a valid email address.");
          } else {
            toast.error("Please check your input and try again.");
          }
        } else {
          toast.error("Please check your input and try again.");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="tech-label mb-4 inline-block">Get In Touch</span>
            <h2
              data-testid="contact-title"
              className="font-syne font-bold text-4xl md:text-5xl text-white mb-6"
            >
              Ready to Break
              <br />
              The Algorithm?
            </h2>
            <p className="font-mono text-zinc-400 text-base leading-relaxed mb-8">
              // Drop us a line. Tell us about your brand, your goals, your
              wildest content dreams. We'll figure out the rest.
            </p>

            <div className="space-y-4 font-mono text-sm">
              <p className="text-zinc-500">
                <span className="text-brand-lime">Email:</span>{" "}
                blancspace.agency@gmail.com
              </p>
              <p className="text-zinc-500">
                <span className="text-brand-lime">Based:</span> We work in
                Algorithms.
              </p>
            </div>
          </div>

          <div className="glass-card rounded-3xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="form-input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="form-input"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  data-testid="contact-company-input"
                  className="form-input"
                  placeholder="Your Brand"
                />
              </div>

              <div>
                <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-message-input"
                  className="form-input resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="cta-button w-full justify-center"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <ArrowUpRight size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative py-16 px-6 md:px-12 lg:px-24 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div>
            <h3 className="font-syne font-bold text-3xl md:text-4xl text-white mb-2">
              BLANC<span className="text-brand-lime">SPACE</span>
            </h3>
            <p className="font-mono text-sm text-zinc-500">
              // Digital Alchemy for the Modern Brand
            </p>
          </div>

          <div className="flex flex-wrap gap-6 font-mono text-sm text-zinc-500">
            <a
              href="#services"
              className="hover:text-brand-lime transition-colors"
            >
              Services
            </a>
            <a
              href="#contact"
              className="hover:text-brand-lime transition-colors"
            >
              Contact
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-mono text-xs text-zinc-600">
            {new Date().getFullYear()} BlancSpace Agency. All rights reserved.
          </p>
          <p className="font-mono text-xs text-zinc-600">
            Engineered with vibes and AI
          </p>
        </div>
      </div>
    </footer>
  );
};

// Landing Page
const LandingPage = () => {
  return (
    <div className="bg-black min-h-screen">
      {/* Noise overlay */}
      <div className="noise-overlay" />

      <Navigation />
      <HeroSection />
      <ServicesSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      {/* Toaster */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />

      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
